import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, catchError, map } from 'rxjs';
import { getApiEndpoint } from '@app/shared/utils/url.utils';
import { parseApiError } from '@app/shared/models/api/api-error.model';
import { SessionModel } from '../models/session.model';
import { Params } from '@angular/router';
import { UserDetails } from '../../addresses/models/user-details.model';
import { PaymentMethod, PaymentProvider } from '../models/payment.provider.model';

@Injectable({ providedIn: 'root' })
export class SessionApiService {
  private readonly http = inject(HttpClient);

  constructor() {}

  public createSessionApi(
    cartId: string | null,
    userId: string | null,
    email: string | null,
    lang: string
  ): Observable<SessionModel | null> {
    // eslint-disable-next-line deprecation/deprecation
    const apiUrl: string = getApiEndpoint('api/v1/checkout/session', true);

    if (!cartId) {
      return throwError(() => parseApiError('No cartId'));
    }

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .post<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Body
        {
          cart_id: cartId,
          customer_id: userId,
          email: email || '',
          client: 'WEB',
          language: lang,
        },
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => SessionModel.fromJson(data))
      );
  }

  public getSessionApi(sessionId: string): Observable<SessionModel | null> {
    const apiUrl: string = getApiEndpoint(`api/v1/checkout/session/${sessionId}`, true);

    if (!sessionId.length) {
      console.log('ERROR: No sessionId');
      return throwError(() => parseApiError('No sessionId'));
    }

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .get<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
          params: {
            session_id: sessionId,
          },
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => SessionModel.fromJson(data))
      );
  }

  public updateSessionApi(
    sessionId: string,
    userDetails: UserDetails | null,
    discountCode?: string | null,
    paymentProvider?: PaymentProvider | null,
    paymentMethod?: PaymentMethod | null
  ): Observable<SessionModel | null> {
    const apiUrl: string = getApiEndpoint(`api/v1/checkout/session/${sessionId}`, true);

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    const body: Params = {};
    if (userDetails) {
      body.customer_details = {
        name: userDetails?.fullName || '',
        contact: {
          phone_country_code: `${userDetails?.contactDetails.countryCode || ''}`,
          phone_area_code: `${userDetails?.contactDetails.areaCode || ''}`,
          phone_number: `${userDetails?.contactDetails.national || ''}`,
        },
      };
      body.shipping_address = {
        country: userDetails?.address?.country || '',
        city: userDetails?.address?.city || '',
        address: userDetails?.address?.address || '',
        address_details: userDetails?.address.addressDetails || '',
        postal_code: userDetails?.address.postalCode || '',
        street_number: userDetails?.address.streetNumber || '',
        province: userDetails?.address?.province || '',
      };
      body.billing_address = {
        country: userDetails?.address?.country || '',
        city: userDetails?.address?.city || '',
        address: userDetails?.address?.address || '',
        address_details: userDetails?.address.addressDetails || '',
        postal_code: userDetails?.address.postalCode || '',
        street_number: userDetails?.address.streetNumber || '',
        province: userDetails?.address?.province || '',
      };
      if (userDetails.email) {
        body.email = userDetails.email || '';
      }
    }

    if (discountCode) {
      body.discount_code = discountCode;
    }

    if (paymentProvider) {
      body.payment_provider = paymentProvider;
    }

    if (paymentMethod) {
      body.payment_method = paymentMethod;
    }

    return this.http
      .put<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Body
        body,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
          params: {
            session_id: sessionId,
          },
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => SessionModel.fromJson(data))
      );
  }

  public updateSessionDiscountApi(
    sessionId: string,
    discountCode?: string | null
  ): Observable<SessionModel | null> {
    const apiUrl: string = getApiEndpoint(
      `api/v1/checkout/session/${sessionId}/discount`,
      true
    );

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    const body: Params = {};

    if (discountCode) {
      body.discount_code = discountCode;
    }

    return this.http
      .put<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Body
        body,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
          params: {
            session_id: sessionId,
          },
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => SessionModel.fromJson(data))
      );
  }
}
